import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const UPLOAD_API_URL = import.meta.env.VITE_UPLOAD_API_URL || '/api/upload.php';
const DELETE_API_URL = import.meta.env.VITE_DELETE_API_URL || '/api/delete.php';

// Max file size: 50MB
const MAX_FILE_SIZE = 50 * 1024 * 1024;

const ALLOWED_EXTENSIONS: Record<string, string[]> = {
  pdf: ['pdf'],
  image: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'],
  audio: ['mp3'],
  video: ['mp4'],
  document: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv'],
  any: ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'mp3', 'mp4', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'zip'],
};

export interface UploadResult {
  success: boolean;
  url: string;
  filename: string;
  originalName: string;
  size: number;
  sizeFormatted: string;
  mimeType: string;
  extension: string;
}

const UPLOAD_COOLDOWN_MS = 3000; // 3 seconds between uploads

export function useFileUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [lastUploadTime, setLastUploadTime] = useState(0);
  const { user } = useAuthStore();

  const getAuthToken = async (): Promise<string | null> => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token || null;
  };

  const uploadFile = async (
    file: File,
    fileType: keyof typeof ALLOWED_EXTENSIONS = 'any',
    customName?: string
  ): Promise<UploadResult | null> => {
    if (!user) {
      toast.error('Please sign in to upload files');
      return null;
    }

    // Rate limiting: enforce cooldown between uploads
    const now = Date.now();
    if (now - lastUploadTime < UPLOAD_COOLDOWN_MS) {
      toast.error('Please wait a moment before uploading again.');
      return null;
    }
    setLastUploadTime(now);

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File too large. Maximum size is 50MB.`);
      return null;
    }

    // Validate extension
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const allowed = ALLOWED_EXTENSIONS[fileType] || ALLOWED_EXTENSIONS.any;
    if (!allowed.includes(ext)) {
      toast.error(`File type .${ext} not allowed. Allowed: ${allowed.join(', ')}`);
      return null;
    }

    const token = await getAuthToken();
    if (!token) {
      toast.error('Authentication expired. Please sign in again.');
      return null;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (customName) {
        formData.append('custom_name', customName);
      }

      // Use XMLHttpRequest for progress tracking
      const result = await new Promise<UploadResult>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percent = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(percent);
          }
        });

        xhr.addEventListener('load', () => {
          try {
            const response = JSON.parse(xhr.responseText);
            if (xhr.status === 200 && response.success) {
              resolve(response as UploadResult);
            } else {
              reject(new Error(response.error || `Upload failed (${xhr.status})`));
            }
          } catch {
            console.error('Upload response:', xhr.status, xhr.responseText?.substring(0, 500));
            reject(new Error(`Server error (${xhr.status}). Please try again.`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload cancelled'));
        });

        xhr.open('POST', UPLOAD_API_URL);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(formData);
      });

      toast.success(`File uploaded successfully!`);
      return result;
    } catch (error: any) {
      toast.error(error.message || 'Upload failed');
      return null;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const deleteFile = async (filename: string): Promise<boolean> => {
    if (!user) return false;

    const token = await getAuthToken();
    if (!token) return false;

    try {
      const response = await fetch(DELETE_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ filename }),
      });

      const data = await response.json();
      if (data.success) {
        return true;
      }
      console.error('Delete failed:', data.error);
      return false;
    } catch (error) {
      console.error('Delete error:', error);
      return false;
    }
  };

  return {
    uploadFile,
    deleteFile,
    isUploading,
    uploadProgress,
  };
}
